export interface ICadesVersion {
  toString(): Promise<string>;
}

export interface ICadesCollection<T> {
  Count: Promise<number>;
  Item(index: number): Promise<T>;
}

export interface ICadesPrivateKey {
  CacheKey(enabled: boolean): Promise<void>;
}

export interface ICadesCertificate {
  SubjectName: Promise<string>;
  IssuerName: Promise<string>;
  SerialNumber: Promise<string>;
  Thumbprint: Promise<string>;
  ValidFromDate: Promise<string>;
  ValidToDate: Promise<string>;
  PrivateKey: Promise<ICadesPrivateKey>;
  HasPrivateKey(): Promise<boolean>;
  IsValid(): Promise<{ Result: Promise<boolean>; ErrorStatuses: Promise<ICadesCollection<number>> }>;
}

export interface ICadesStore {
  Open(location: number, name?: string, mode?: number): Promise<void>;
  Certificates: Promise<ICadesCollection<ICadesCertificate>>;
  Close(): Promise<void>;
}

export interface ICadesSigner {
  propset_Certificate(certificate: ICadesCertificate): Promise<void>;
  propset_Options(options: number): Promise<void>;
}

export interface ICadesObjects {
  'CAdESCOM.About': { PluginVersion: Promise<ICadesVersion>; CSPVersion(): Promise<ICadesVersion> };
  'CAdESCOM.Store': ICadesStore;
  'CAdESCOM.CPLicense': { IsValid(): Promise<boolean>; ValidTo(): Promise<string> };
  'CAdESCOM.CPSigner': ICadesSigner;
  'CAdESCOM.CadesSignedData': {
    propset_ContentEncoding(encoding: number): Promise<void>;
    propset_Content(content: string): Promise<void>;
    SignCades(signer: ICadesSigner, type: number, detached: boolean): Promise<string>;
  };
}

export interface ICadesPlugin extends Promise<void> {
  CreateObjectAsync<K extends keyof ICadesObjects>(name: K): Promise<ICadesObjects[K]>;
  getLastError(error: unknown): string;
  CAPICOM_CURRENT_USER_STORE: number;
  CAPICOM_MY_STORE: string;
  CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED: number;
  CADESCOM_CONTAINER_STORE: number;
  CADESCOM_BASE64_TO_BINARY: number;
  CADESCOM_CADES_BES: number;
  CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY: number;
}

export enum CertificateStore {
  Personal = 'personal',
  Container = 'container',
}

export interface ICertificateIdentity {
  snils: string;
  inn: string;
  innLe: string;
  lastName: string;
  firstName: string;
  middleName: string;
  position: string;
  commonName: string;
}

export interface ICertificate {
  certificate: ICadesCertificate;
  identity: ICertificateIdentity;
  subject: string;
  issuer: string;
  serial: string;
  thumbprint: string;
  validFrom: string;
  validTo: string;
  valid: boolean;
  hasPrivateKey: boolean;
  errorStatuses: number[];
  error?: string;
  statusError?: string;
}

export type CadesCheckKey = 'plugin' | 'pluginVersion' | 'cspVersion' | 'store' | 'certificates' | 'license';
export type CadesHint =
  | 'plugin'
  | 'pluginVersion'
  | 'cspVersion'
  | 'store'
  | 'certificates'
  | 'license'
  | 'trustedSite';

export interface ICadesCheck {
  key: CadesCheckKey;
  status: 'waiting' | 'success' | 'error' | 'unavailable';
  value?: string;
  error?: string;
  hint?: CadesHint;
}

export interface IComputerCheck {
  checks: ICadesCheck[];
  certificates: ICertificate[];
  ready: boolean;
}
